import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { userApi, postApi } from '../utils/api';
import { User, Post } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { MessageCircle, Eye, Heart } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguageStore } from '../stores/languageStore';

const UserProfile: React.FC = () => {
  const { language } = useLanguageStore();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = React.useState<User | null>(null);
  const [posts, setPosts] = React.useState<Post[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);

  const t = (key: string) => {
    const translations: Record<string, Record<string, string>> = {
      zh: {
        '等级': '等级',
        '积分': '积分',
        '帖子': '帖子',
        '回复': '回复',
        '注册于': '注册于',
        '发布的帖子': '发布的帖子',
        '暂无帖子': '暂无帖子',
        '上一页': '上一页',
        '下一页': '下一页',
      },
      en: {
        '等级': 'Level',
        '积分': 'Points',
        '帖子': 'Posts',
        '回复': 'Replies',
        '注册于': 'Registered',
        '发布的帖子': 'Published Posts',
        '暂无帖子': 'No posts yet',
        '上一页': 'Previous',
        '下一页': 'Next',
      }
    };
    return translations[language]?.[key] || key;
  };

  React.useEffect(() => {
    if (id) {
      fetchUser();
      fetchPosts();
    }
  }, [id, page]);

  const fetchUser = async () => {
    try {
      const response = await userApi.getUserProfile(parseInt(id || '0'));
      if (response.success && response.data) {
        setUser(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await postApi.getUserPosts(parseInt(id || '0'), { page, pageSize: 10 });
      if (response.success) {
        setPosts(response.data || []);
        setTotalPages(response.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: language === 'en' ? enUS : zhCN });
    } catch {
      return dateString;
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 用户信息 */}
      <div className="card p-6">
        <div className="flex items-start gap-6">
          <img
            src={user.avatar || `https://ui-avatars.com/api/?name=${user.nickname || user.username || 'User'}&background=3b82f6&color=fff`}
            alt={user.nickname || user.username}
            className="w-24 h-24 rounded-full"
          />
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{user.nickname}</h1>
            <p className="text-gray-500 mt-1">@{user.username}</p>
            {user.signature && (
              <p className="text-gray-600 mt-2">{user.signature}</p>
            )}
            <div className="flex items-center gap-6 mt-4">
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900">{user.level}</div>
                <div className="text-sm text-gray-500">{t('等级')}</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900">{user.points}</div>
                <div className="text-sm text-gray-500">{t('积分')}</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900">{user.posts_count}</div>
                <div className="text-sm text-gray-500">{t('帖子')}</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-gray-900">{user.replies_count}</div>
                <div className="text-sm text-gray-500">{t('回复')}</div>
              </div>
            </div>
            <div className="text-sm text-gray-500 mt-4">
              {t('注册于')} {formatDate(user.created_at)}
            </div>
          </div>
        </div>
      </div>

      {/* 用户帖子 */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('发布的帖子')}</h2>

        {posts.length > 0 ? (
          <div className="space-y-4">
            {posts.map((post) => (
              <article key={post.id} className="p-4 bg-gray-50 rounded-lg">
                <Link
                  to={`/post/${post.id}`}
                  className="text-lg font-semibold text-gray-900 hover:text-primary-600"
                >
                  {post.title}
                </Link>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  <span className="flex items-center">
                    <Eye className="w-4 h-4 mr-1" />
                    {post.views}
                  </span>
                  <span className="flex items-center">
                    <MessageCircle className="w-4 h-4 mr-1" />
                    {post.replies_count}
                  </span>
                  <span className="flex items-center">
                    <Heart className="w-4 h-4 mr-1" />
                    {post.likes}
                  </span>
                  <span>{formatDate(post.created_at)}</span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">{t('暂无帖子')}</p>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              {t('上一页')}
            </button>
            <span className="text-sm text-gray-600">
              第 {page} / {totalPages} 页
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              {t('下一页')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
